import { put, take, fork, call, select, all } from "redux-saga/effects";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import { ScopeErrorAction } from "../scope-error/types";
import { PendingContractAction, PendingContractScope } from "./types";
import {
  cleanUpPendingContract,
  setPendingContractRecord,
  setUploadedDocuments,
} from "./actions";
import * as API from "../../service/api";
import { mapApiPendingContract } from "./helper";
import { handleApiErrorToast } from "../../helper";
import axios from "axios";
import toast from "react-hot-toast";
import { RootState } from "../root-reducer";

function* fetchPendingContractRecords(): Generator<any, void, any> {
  while (true) {
    const { payload = "" } = yield take(
      PendingContractAction.GET_PENDING_CONTRACT_RECORD
    );
    yield put(enableScopeLoading(PendingContractScope));
    try {
      if (!payload) throw new Error("Id is required");
      const { data } = yield call(
        API.contract.getPendingContractRecordsById,
        payload
      );
      yield put(setPendingContractRecord(mapApiPendingContract(data)));
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: PendingContractScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(PendingContractScope));
    }
  }
}

function* createPendingContractFlow(): Generator<any, void, any> {
  while (true) {
    const { payload, callback } = yield take(
      PendingContractAction.CREATE_PENDING_CONTRACT
    );
    yield put(enableScopeLoading(PendingContractScope));
    try {
      const documents = yield select(
        (state: RootState) => state.pendingContract.documents
      );
      payload.documentation = documents.map((fileUrl) => ({
        s3_url: fileUrl,
        file_name: fileUrl?.split("/")?.pop() || "",
      }));
      payload.source = "dealer_portal";
      const data = yield call(API.contract.insertContractItem, payload);
      if (data?.contract_id) {
        yield put(cleanUpPendingContract());
        callback();
      } else {
        toast.error(data?.message || "Failed to create contract", {
          position: "bottom-center",
        });
      }
    } catch (error: any) {
      handleApiErrorToast(error, "Failed to create contract");
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: PendingContractScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(PendingContractScope));
    }
  }
}

function* uploadInvoiceFlow(): Generator<any, void, any> {
  while (true) {
    const { files, callback } = yield take(
      PendingContractAction.UPLOAD_DOCUMENTS
    );
    yield put(enableScopeLoading(PendingContractScope));
    try {
      const uploadFileUrls = yield all(
        files.map(function* (file) {
          const { url: uploadURL } = yield call(
            API.contract.getPresignedURL,
            "invoice-files",
            file.type,
            file.name.split(".").pop()
          );
          yield call(axios.put, uploadURL, file, {
            headers: {
              "Content-Type": file.type,
            },
          });
          const urlObj = new URL(uploadURL);
          urlObj.search = "";
          return urlObj.toString();
        })
      );
      yield put(setUploadedDocuments(uploadFileUrls));
      callback();
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: PendingContractScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(PendingContractScope));
    }
  }
}

function* importPendingContractFlow(): Generator<any, void, any> {
  while (true) {
    const { file, dealerId, callback } = yield take(
      PendingContractAction.IMPORT_PENDING_CONTRACT
    );
    yield put(enableScopeLoading(PendingContractScope));
    try {
      const { url: uploadURL } = yield call(
        API.contract.getPresignedURL,
        "import-files",
        file.type,
        file.name.split(".").pop(),
        dealerId
      );
      if (!uploadURL) throw new Error("Upload URL not found");
      yield axios.put(uploadURL, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      const urlObj = new URL(uploadURL);
      urlObj.search = "";
      const uploadedFileName = urlObj.toString().split("/").pop();
      const data = yield call(API.contract.importContractItem, {
        file_name: uploadedFileName,
        dealer_id: dealerId,
      });
      callback(data?.total_created_records, data?.invalid_row_invoice_number);
    } catch (error) {
      handleApiErrorToast(error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: PendingContractScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(PendingContractScope));
    }
  }
}

export default function* pendingContractSaga(): Generator<any, void, any> {
  yield fork(fetchPendingContractRecords);
  yield fork(createPendingContractFlow);
  yield fork(uploadInvoiceFlow);
  yield fork(importPendingContractFlow);
}
